import React, { useState, useEffect } from "react";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../components/ui/dialog";
import {
    MagnifyingGlassIcon,
    PlusIcon,
    BuildingOffice2Icon,
    UsersIcon,
} from "@heroicons/react/24/outline";
import { Customer, Division } from "../../../types/settings";
import {
    getCustomers,
    getDivisions,
    createCustomer,
    createDivision,
} from "./api/customer";

const Customers: React.FC = () => {
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
    const [divisions, setDivisions] = useState<Division[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [divisionsLoading, setDivisionsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const [searchTerm, setSearchTerm] = useState<string>("");

    const [createCustomerModalOpen, setCreateCustomerModalOpen] = useState<boolean>(false);
    const [createCustomerLoading, setCreateCustomerLoading] = useState<boolean>(false);
    const [createCustomerError, setCreateCustomerError] = useState<string>("");
    const [customerData, setCustomerData] = useState({
        cust_id: "",
        cust_name: "",
    });

    const [createDivisionModalOpen, setCreateDivisionModalOpen] = useState<boolean>(false);
    const [createDivisionLoading, setCreateDivisionLoading] = useState<boolean>(false);
    const [createDivisionError, setCreateDivisionError] = useState<string>("");
    const [divisionId, setDivisionId] = useState<string>("");
    const [divisionName, setDivisionName] = useState<string>("");

    useEffect(() => {
        const fetchCustomers = async () => {
            try {
                setLoading(true);
                setError("");
                const response = await getCustomers();
                setCustomers(response || []);
            } catch (err: any) {
                console.error("Error fetching customers:", err);
                setError(err?.message || "Failed to fetch customers");
            } finally {
                setLoading(false);
            }
        };

        fetchCustomers();
    }, []);

    useEffect(() => {
        const fetchDivisions = async () => {
            if (!selectedCustomer) {
                setDivisions([]);
                return;
            }

            try {
                setDivisionsLoading(true);
                const response = await getDivisions(selectedCustomer.cust_id);
                setDivisions(response || []);
            } catch (err) {
                console.error("Error fetching divisions:", err);
                setDivisions([]);
            } finally {
                setDivisionsLoading(false);
            }
        };

        fetchDivisions();
    }, [selectedCustomer]);

    const filteredCustomers = customers.filter(
        (c) =>
            c.cust_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            c.cust_id.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleCustomerSelect = (customer: Customer) => {
        setSelectedCustomer(customer);
    };

    const handleOpenCreateCustomerModal = () => {
        setCreateCustomerModalOpen(true);
        setCreateCustomerError("");
        setCustomerData({ cust_id: "", cust_name: "" });
    };

    const handleCloseCreateCustomerModal = () => {
        setCreateCustomerModalOpen(false);
        setCreateCustomerError("");
        setCustomerData({ cust_id: "", cust_name: "" });
    };

    const handleCreateCustomer = async () => {
        try {
            setCreateCustomerLoading(true);
            setCreateCustomerError("");

            if (!customerData.cust_id.trim())
                return setCreateCustomerError("Customer ID is required");
            if (!customerData.cust_name.trim())
                return setCreateCustomerError("Customer name is required");

            await createCustomer({
                cust_id: customerData.cust_id.trim(),
                cust_name: customerData.cust_name.trim(),
            });

            const response = await getCustomers();
            setCustomers(response || []);
            handleCloseCreateCustomerModal();
        } catch (err: any) {
            console.error(err);
            setCreateCustomerError(err?.message || "Failed to create customer");
        } finally {
            setCreateCustomerLoading(false);
        }
    };

    const handleOpenCreateDivisionModal = () => {
        if (!selectedCustomer) return;
        setCreateDivisionModalOpen(true);
        setCreateDivisionError("");
        setDivisionId("");
        setDivisionName("");
    };

    const handleCloseCreateDivisionModal = () => {
        setCreateDivisionModalOpen(false);
        setCreateDivisionError("");
        setDivisionId("");
        setDivisionName("");
    };

    const handleCreateDivision = async () => {
        if (!selectedCustomer) return;

        try {
            setCreateDivisionLoading(true);
            setCreateDivisionError("");

            if (!divisionId.trim())
                return setCreateDivisionError("Division ID required");
            if (!divisionName.trim())
                return setCreateDivisionError("Division name required");

            await createDivision({
                cust_id: selectedCustomer.cust_id,
                divn_id: Number(divisionId),
                divn_name: divisionName.trim(),
            });

            const response = await getDivisions(selectedCustomer.cust_id);
            setDivisions(response || []);
            handleCloseCreateDivisionModal();
        } catch (err: any) {
            console.error(err);
            setCreateDivisionError(err?.message || "Failed to create division");
        } finally {
            setCreateDivisionLoading(false);
        }
    };

    return (
        <div className="flex h-[calc(100vh-200px)] gap-6">
            <div className="w-1/3 flex flex-col h-full min-h-0">
                <Card className="flex flex-col flex-1 min-h-0">
                    <CardHeader className="pb-4">
                        <CardTitle className="text-base font-medium">
                            Customers
                        </CardTitle>
                    </CardHeader>

                    <CardContent className="flex flex-col flex-1 space-y-4 min-h-0">
                        <div className="relative">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input
                                className="pl-10"
                                placeholder="Search customers..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <Button
                            className="w-full"
                            variant="outline"
                            onClick={handleOpenCreateCustomerModal}
                        >
                            <PlusIcon className="w-4 h-4 mr-2" />
                            Create Customer
                        </Button>

                        <div className="flex-1 space-y-2 overflow-y-auto">
                            {loading ? (
                                <div className="flex justify-center py-8">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-500"></div>
                                </div>
                            ) : error ? (
                                <Alert variant="destructive">
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            ) : filteredCustomers.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    No customers found
                                </div>
                            ) : (
                                filteredCustomers.map((customer) => (
                                    <div
                                        key={customer.cust_id}
                                        onClick={() => handleCustomerSelect(customer)}
                                        className={`p-3 rounded-lg border cursor-pointer transition-all ${selectedCustomer?.cust_id === customer.cust_id
                                                ? "border-blue-200 bg-blue-50 text-blue-900"
                                                : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                                            }`}
                                    >
                                        <div className="flex items-center space-x-3">
                                            <BuildingOffice2Icon className="w-4 h-4" />
                                            <div className="min-w-0 flex-1">
                                                <div className="font-medium text-sm truncate">
                                                    {customer.cust_name}
                                                </div>
                                                <div className="text-xs text-gray-500 truncate">
                                                    ID: {customer.cust_id}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* RIGHT PANEL */}
            <div className="flex-1 flex flex-col">
                <Card className="flex-1">
                    <CardHeader className="pb-4">
                        <div className="flex justify-between items-center">
                            <CardTitle className="text-base font-medium">
                                {selectedCustomer
                                    ? `Divisions - ${selectedCustomer.cust_name}`
                                    : "Select a Customer"}
                            </CardTitle>

                            {selectedCustomer && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={handleOpenCreateDivisionModal}
                                >
                                    <PlusIcon className="w-4 h-4 mr-2" />
                                    Create Division
                                </Button>
                            )}
                        </div>
                    </CardHeader>

                    <CardContent>
                        {!selectedCustomer ? (
                            <div className="flex justify-center items-center h-64 text-gray-500">
                                <div className="text-center">
                                    <BuildingOffice2Icon className="w-8 h-8 mx-auto mb-4 text-gray-300" />
                                    <p>Select a customer to view divisions</p>
                                </div>
                            </div>
                        ) : divisionsLoading ? (
                            <div className="flex justify-center items-center h-64">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-500"></div>
                            </div>
                        ) : divisions.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                                No divisions found
                            </div>
                        ) : (
                            <div className="space-y-2 max-h-96 overflow-y-auto">
                                {divisions.map((d) => (
                                    <div
                                        key={d.divn_id}
                                        className="p-3 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 flex items-center space-x-3"
                                    >
                                        <UsersIcon className="w-4 h-4 text-gray-500" />
                                        <div className="font-medium text-sm">
                                            {d.divn_name}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            <Dialog
                open={createCustomerModalOpen}
                onOpenChange={setCreateCustomerModalOpen}
            >
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Create New Customer</DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4">
                        {createCustomerError && (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {createCustomerError}
                                </AlertDescription>
                            </Alert>
                        )}

                        <div className="space-y-2">
                            <Label>Customer ID</Label>
                            <Input
                                value={customerData.cust_id}
                                onChange={(e) =>
                                    setCustomerData((p) => ({
                                        ...p,
                                        cust_id: e.target.value,
                                    }))
                                }
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Customer Name</Label>
                            <Input
                                value={customerData.cust_name}
                                onChange={(e) =>
                                    setCustomerData((p) => ({
                                        ...p,
                                        cust_name: e.target.value,
                                    }))
                                }
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={handleCloseCreateCustomerModal}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleCreateCustomer}
                            disabled={createCustomerLoading}
                        >
                            {createCustomerLoading ? "Creating..." : "Create Customer"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog
                open={createDivisionModalOpen}
                onOpenChange={setCreateDivisionModalOpen}
            >
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>
                            Create Division - {selectedCustomer?.cust_name}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4">
                        {createDivisionError && (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {createDivisionError}
                                </AlertDescription>
                            </Alert>
                        )}

                        <div className="space-y-2">
                            <Label>Division ID</Label>
                            <Input
                                type="number"
                                value={divisionId}
                                onChange={(e) => setDivisionId(e.target.value)}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Division Name</Label>
                            <Input
                                value={divisionName}
                                onChange={(e) => setDivisionName(e.target.value)}
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={handleCloseCreateDivisionModal}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleCreateDivision}
                            disabled={createDivisionLoading}
                        >
                            {createDivisionLoading ? "Creating..." : "Create Division"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Customers;
